import type { Language } from "../../contexts/LanguageContext";

export const apptLabels: Record<Language, {
  common: {
    loading: string;
    close: string;
    filters: string;
    professional: string;
    all: string;
    showDone: string;
    showPastToday: string;
    newAppointment: string;
    newRecurring: string;
    mobileNewSingle: string;
    mobileNewRecurring: string;
    cancel: string;
    back: string;
    save: string;
    delete: string;
    showSaturday: string;
    showSunday: string;
  };
  toasts: {
    updateError: string;
    markedDone: string;
    unmarked: string;
    deleted: string;
    deleteError: string;
    recurringDeleted: string;
    recurringDeleteError: string;
  };
  header: {
    filters: string;
    close: string;
  };
  todayList: {
    title: string;
  };
  card: {
    defaultTitle: string;
    done: string;
    notDone: string;
    fillEvolution: string;
    reschedule: string;
    markDone: string;
    unmarkDone: string;
    pendingPayment: string;
    viewClient: string;
  };
  daySchedule: {
    tooltips: { adjustStart: string; adjustEnd: string };
    menu: { markDone: string; unmarkDone: string; delete: string };
  };
  monthCalendar: {
    weekdayShort: string[]; // length 7, starting Sunday
    locale: string; // Intl locale for header
  };
  weekGrid: {
    empty: string;
  };
  slotSelector: {
    loading: string;
  };
  appSections: {
    happeningNow: string;
    noneInProgress: string;
    pastTitle: string;
    nonePast: string;
    upcomingTitle: string;
    noneUpcoming: string;
  };
  editModal: {
    title: string;
    professional: string;
    client: string;
    select: string;
    specialty: string;
    date: string;
    start: string;
    end: string;
    previewNote: string;
    markDone: string;
    recurrence: {
      title: string;
      description: string;
      onlyThis: string;
      allNext: string;
    };
    deleteDialog: {
      title: string;
      description: string;
      cancel: string;
      deleteAllRecurring: string;
      delete: string;
    };
  };
  newPage: {
    title: string;
    professional: string;
    client: string;
    specialty: string;
    insurance: string;
    paidByInsurance: string;
    paymentWillBeByInsurance: string;
    date: string;
    start: string;
    end: string;
    amount: string; // include currency symbol in label or keep generic
    amountPlaceholder: string;
    paidNow: string;
    createError: string;
    created: string;
    comboboxSearch: string;
    comboboxEmpty: string;
    conflictsTitle: string;
    selectTimeHint: string;
    invalidRange: string;
    noConflicts: string;
    conflictSections: {
      sameProfessional: string;
      others: string;
    };
  };
  editPage: {
    invalid: string;
    loading: string;
    updated: string;
    saveError: string;
  };
  createDrawer: {
    title: string;
    success: string;
    error: string;
    appointmentType: string;
    typeSingle: string;
    typeRecurring: string;
    addSlot: string;
    frequency: string;
    weekdays: string[];
    frequencyOptions: {
      weekly: string;
    };
    endType: string;
    endTypeOptions: {
      never: string;
      weeks: string;
    };
    weeksLabel: string;
    endDate: string;
    payment: {
      title: string;
      amount: string;
      status: string;
      statusOptions: {
        pending: string;
        paid: string;
      };
      billingDay: string;
      isProportional: string;
      proportionalType: string;
      typeAmount: string;
      typePercentage: string;
      firstPaymentAmount: string;
      firstPaymentPercentage: string;
      firstPaymentDate: string;
      billingSummary: (date: string, amount: string, regularAmount: string, day: number) => string;
    };
  };
  publicReschedule: {
    welcome: (name: string) => string;
    subtitle: string;
    currentAppt: string;
    newDateLabel: string;
    professionalLabel: string;
    closedPage: string;
    invalidLink: string;
    tryAgain: string;
    rescheduled: string;
    rescheduledSubtitle: string;
    step1: string;
    step2: string;
    step3: string;
    searching: string;
    noSlots: string;
    tip: string;
    selected: string;
    confirm: string;
    processing: string;
    specialtyLabel: string;
    at: string;
  },
  newMensalidadePage: {
    professional: string;
    client: string;
    specialty: string;
    insurance: string;
    planValue: string;
    billingPeriod: string;
    billingDay: string;
    planName: string;
    markFirstAsPaid: string;
    weekday: string;
    start: string;
    end: string;
    createForToday: string;
    removeDay: string;
    addDay: string;
    cancel: string;
    save: string;
    selectPlaceholder: string;
    comboboxSearch: string;
    comboboxEmpty: string;
    billingPeriods: {
      monthly: string;
      quarterly: string;
      semiannual: string;
      annual: string;
    };
    weekdays: string[];
    paidByInsurance: string;
  };
  evolutionModal: {
    title: string;
    audio: {
      title: string;
      subtitle: string;
      buttonStart: string;
      recording: string;
      buttonStop: string;
      converting: string;
    };
    content: {
      label: string;
      improve: string;
      improving: string;
      placeholder: string;
    };
    actions: {
      cancel: string;
      save: string;
    };
    toasts: {
      success: string;
      error: string;
      audioConverted: string;
      audioError: string;
      aiError: string;
    };
  };
}> = {
  pt: {
    common: {
      loading: "Carregando...",
      close: "Fechar",
      filters: "Filtros",
      professional: "Profissional:",
      all: "Todos",
      showDone: "Mostrar finalizadas",
      showPastToday: "Mostrar passadas (hoje)",
      newAppointment: "Nova consulta",
      newRecurring: "Nova mensalidade",
      mobileNewSingle: "Consulta única",
      mobileNewRecurring: "Recorrente",
      cancel: "Cancelar",
      back: "Voltar",
      save: "Salvar",
      delete: "Apagar",
      showSaturday: "Mostrar sábado",
      showSunday: "Mostrar domingo",
    },
    toasts: {
      updateError: "Erro ao atualizar",
      markedDone: "Consulta concluída",
      unmarked: "Conclusão desmarcada",
      deleted: "Consulta apagada",
      deleteError: "Erro ao apagar",
      recurringDeleted: "Consultas recorrentes apagadas",
      recurringDeleteError: "Erro ao apagar consultas recorrentes",
    },
    header: {
      filters: "Filtros",
      close: "Fechar",
    },
    todayList: {
      title: "Próximas consultas de hoje",
    },
      card: {
        defaultTitle: "Consulta",
        done: "Realizada",
        notDone: "Não realizada",
        fillEvolution: "Preencher evolução",
        reschedule: "Remarcar",
        markDone: "Marcar como realizada",
        unmarkDone: "Desmarcar realizada",
        pendingPayment: "Financeiro Pendente",
        viewClient: "Ver Cliente",
      },
    daySchedule: {
      tooltips: { adjustStart: "Ajustar início", adjustEnd: "Ajustar fim" },
      menu: { markDone: "Marcar conclusão", unmarkDone: "Desmarcar conclusão", delete: "Excluir" },
    },
    monthCalendar: {
      weekdayShort: ["D", "S", "T", "Q", "Q", "S", "S"],
      locale: "pt-BR",
    },
    weekGrid: {
      empty: "Sem atendimentos",
    },
    slotSelector: { loading: "Carregando..." },
    appSections: {
      happeningNow: "Acontecendo agora",
      noneInProgress: "Nenhuma consulta em andamento.",
      pastTitle: "Consultas passadas",
      nonePast: "Nenhuma consulta passada hoje.",
      upcomingTitle: "Próximas consultas",
      noneUpcoming: "Nenhuma consulta futura para hoje.",
    },
    editModal: {
      title: "Editar consulta",
      professional: "Profissional",
      client: "Cliente",
      select: "Selecione",
      specialty: "Especialidade",
      date: "Data",
      start: "Início",
      end: "Fim",
      previewNote: "Observações",
      markDone: "Concluir Agendamento",
      recurrence: {
        title: "Editar recorrência",
        description: "Deseja aplicar as alterações somente nesta consulta ou em todas as próximas?",
        onlyThis: "Somente esta",
        allNext: "Todas as próximas",
      },
      deleteDialog: {
        title: "Excluir consulta",
        description: "Deseja realmente apagar este agendamento?",
        cancel: "Cancelar",
        deleteAllRecurring: "Excluir todas as recorrentes",
        delete: "Excluir",
      },
    },
    newPage: {
      title: "Nova consulta",
      professional: "Profissional",
      client: "Cliente",
      specialty: "Especialidade",
      insurance: "Convênio",
      paidByInsurance: "Pago pelo convênio",
      paymentWillBeByInsurance: "Pagamento será feito pelo convênio",
      date: "Data",
      start: "Início",
      end: "Fim",
      amount: "Valor (R$)",
      amountPlaceholder: "0,00",
      paidNow: "Pagamento já realizado",
      createError: "Erro ao criar",
      created: "Consulta criada",
      comboboxSearch: "Buscar...",
      comboboxEmpty: "Nenhum item encontrado.",
      conflictsTitle: "Consultas no mesmo horário",
      selectTimeHint: "Selecione horário de início e fim para verificar conflitos.",
      invalidRange: "O horário final deve ser maior que o horário inicial.",
      noConflicts: "Nenhuma consulta neste intervalo.",
      conflictSections: {
        sameProfessional: "Mesmo profissional",
        others: "Outras consultas",
      },
    },
    editPage: {
      invalid: "Consulta inválida",
      loading: "Carregando...",
      updated: "Consulta atualizada",
      saveError: "Erro ao salvar",
    },
    createDrawer: {
      title: "Novo Agendamento",
      success: "Agendamento criado com sucesso",
      error: "Erro ao criar agendamento",
      appointmentType: "Tipo de Agendamento",
      typeSingle: "Consulta Única",
      typeRecurring: "Plano Mensal (Recorrente)",
      addSlot: "Adicionar outro dia/horário",
      frequency: "Frequência",
      weekdays: ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"],
      frequencyOptions: {
        weekly: "Semanal",
      },
      endType: "Quando termina?",
      endTypeOptions: {
        never: "Até cancelar (Indeterminado)",
        weeks: "Número determinado de semanas",
      },
      weeksLabel: "Quantas semanas?",
      endDate: "Data de término",
      payment: {
        title: "Pagamento",
        amount: "Valor (R$)",
        status: "Status do pagamento",
        statusOptions: {
          pending: "Pendente",
          paid: "Pago",
        },
        billingDay: "Dia de vencimento mensal",
        isProportional: "Primeira fatura proporcional (pro-rata)",
        proportionalType: "Tipo de cobrança",
        typeAmount: "Valor (R$)",
        typePercentage: "Percentual (%)",
        firstPaymentAmount: "Valor da 1ª fatura",
        firstPaymentPercentage: "Percentual da 1ª fatura (%)",
        firstPaymentDate: "Vencimento da 1ª fatura",
        billingSummary: (date, amount, regularAmount, day) => `Primeira fatura dia ${date} no valor de R$ ${amount}. Após isso, R$ ${regularAmount} todo dia ${day} do mês.`,
      },
    },
    publicReschedule: {
      welcome: (name) => `Olá, ${name}`,
      subtitle: "Escolha um novo horário para seu atendimento.",
      currentAppt: "Agendamento Atual",
      newDateLabel: "Nova Data",
      professionalLabel: "Profissional",
      closedPage: "Já pode fechar esta página.",
      invalidLink: "Link Inválido",
      tryAgain: "Tentar Novamente",
      rescheduled: "Remarcado!",
      rescheduledSubtitle: "Sua consulta foi remarcada com sucesso. Você receberá uma confirmação em breve.",
      step1: "Com quem você deseja ser atendido?",
      step2: "Escolha o dia",
      step3: "Escolha o horário",
      searching: "Buscando horários...",
      noSlots: "Infelizmente não há horários livres para este profissional neste dia.",
      tip: "Dica: Tente outro profissional ou outra data",
      selected: "Selecionado",
      confirm: "Confirmar Remarcação",
      processing: "Processando...",
      specialtyLabel: "Especialidade",
      at: "às",
    },
    newMensalidadePage: {
      professional: "Profissional",
      client: "Cliente",
      specialty: "Especialidade",
      insurance: "Convênio",
      planValue: "Valor do plano",
      billingPeriod: "Período",
      billingDay: "Dia de pagamento",
      planName: "Nome do plano",
      markFirstAsPaid: "Marcar primeira mensalidade como paga",
      weekday: "Dia da semana",
      start: "Início",
      end: "Fim",
      createForToday: "Criar para o dia atual (horário já passou)",
      removeDay: "Remover dia",
      addDay: "Adicionar dia da semana",
      cancel: "Cancelar",
      save: "Salvar",
      selectPlaceholder: "Selecione",
      comboboxSearch: "Buscar...",
      comboboxEmpty: "Nenhum item encontrado.",
      billingPeriods: {
        monthly: "Mensal",
        quarterly: "Trimestral",
        semiannual: "Semestral",
        annual: "Anual",
      },
      weekdays: ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"],
      paidByInsurance: "Pago pelo convênio",
    },
    evolutionModal: {
      title: "Evolução da consulta",
      audio: {
        title: "Gravar por voz",
        subtitle: "Fale e a IA transcreve para você",
        buttonStart: "Começar",
        recording: "Gravando...",
        buttonStop: "Parar",
        converting: "Convertendo áudio em texto profissional...",
      },
      content: {
        label: "Descrição da Evolução",
        improve: "Melhorar com IA",
        improving: "Melhorando...",
        placeholder: "Descreva a evolução do paciente aqui ou use a gravação por voz acima...",
      },
      actions: {
        cancel: "Cancelar",
        save: "Salvar Evolução",
      },
      toasts: {
        success: "Evolução salva!",
        error: "Erro ao salvar",
        audioConverted: "Áudio convertido!",
        audioError: "Erro na conversão de áudio",
        aiError: "Erro ao usar IA",
      },
    },
  },
  en: {
    common: {
      loading: "Loading...",
      close: "Close",
      filters: "Filters",
      professional: "Professional:",
      all: "All",
      showDone: "Show completed",
      showPastToday: "Show past (today)",
      newAppointment: "New appointment",
      newRecurring: "New recurring plan",
      mobileNewSingle: "Single appointment",
      mobileNewRecurring: "Recurring",
      cancel: "Cancel",
      back: "Back",
      save: "Save",
      delete: "Delete",
      showSaturday: "Show Saturday",
      showSunday: "Show Sunday",
    },
    toasts: {
      updateError: "Error updating",
      markedDone: "Appointment completed",
      unmarked: "Completion unmarked",
      deleted: "Appointment deleted",
      deleteError: "Error deleting",
      recurringDeleted: "Recurring appointments deleted",
      recurringDeleteError: "Error deleting recurring appointments",
    },
    header: {
      filters: "Filters",
      close: "Close",
    },
    todayList: {
      title: "Today's upcoming appointments",
    },
      card: {
        defaultTitle: "Appointment",
        done: "Completed",
        notDone: "Not completed",
        fillEvolution: "Fill evolution",
        reschedule: "Reschedule",
        markDone: "Mark as done",
        unmarkDone: "Unmark as done",
        pendingPayment: "Pending Payment",
        viewClient: "View Client",
      },
    daySchedule: {
      tooltips: { adjustStart: "Adjust start", adjustEnd: "Adjust end" },
      menu: { markDone: "Mark completed", unmarkDone: "Unmark completed", delete: "Delete" },
    },
    monthCalendar: {
      weekdayShort: ["S", "M", "T", "W", "T", "F", "S"],
      locale: "en-US",
    },
    weekGrid: {
      empty: "No appointments",
    },
    slotSelector: { loading: "Loading..." },
    appSections: {
      happeningNow: "Happening now",
      noneInProgress: "No appointments in progress.",
      pastTitle: "Past appointments",
      nonePast: "No past appointments today.",
      upcomingTitle: "Upcoming appointments",
      noneUpcoming: "No upcoming appointments today.",
    },
    editModal: {
      title: "Edit appointment",
      professional: "Professional",
      client: "Client",
      select: "Select",
      specialty: "Specialty",
      date: "Date",
      start: "Start",
      end: "End",
      previewNote: "Notes",
      markDone: "Mark as Done",
      recurrence: {
        title: "Edit recurrence",
        description: "Apply changes only to this appointment or to all next ones?",
        onlyThis: "Only this",
        allNext: "All next",
      },
      deleteDialog: {
        title: "Delete appointment",
        description: "Do you really want to delete this appointment?",
        cancel: "Cancel",
        deleteAllRecurring: "Delete all recurring",
        delete: "Delete",
      },
    },
    newPage: {
      title: "New appointment",
      professional: "Professional",
      client: "Client",
      specialty: "Specialty",
      insurance: "Insurance",
      paidByInsurance: "Paid by insurance",
      paymentWillBeByInsurance: "Payment will be made by insurance",
      date: "Date",
      start: "Start",
      end: "End",
      amount: "Amount (R$)",
      amountPlaceholder: "0.00",
      paidNow: "Payment already made",
      createError: "Error creating",
      created: "Appointment created",
      comboboxSearch: "Search...",
      comboboxEmpty: "No items found.",
      conflictsTitle: "Appointments in the selected time",
      selectTimeHint: "Select start and end times to check for conflicts.",
      invalidRange: "The end time must be later than the start time.",
      noConflicts: "No appointments in this interval.",
      conflictSections: {
        sameProfessional: "Same professional",
        others: "Other appointments",
      },
    },
    editPage: {
      invalid: "Invalid appointment",
      loading: "Loading...",
      updated: "Appointment updated",
      saveError: "Error saving",
    },
    createDrawer: {
      title: "New Appointment",
      success: "Appointment created successfully",
      error: "Error creating appointment",
      appointmentType: "Appointment Type",
      typeSingle: "Single Appointment",
      typeRecurring: "Monthly Plan (Recurring)",
      addSlot: "Add another day/time",
      frequency: "Frequency",
      weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      frequencyOptions: {
        weekly: "Weekly",
      },
      endType: "When does it end?",
      endTypeOptions: {
        never: "Until canceled",
        weeks: "Fixed number of weeks",
      },
      weeksLabel: "How many weeks?",
      endDate: "End date",
      payment: {
        title: "Payment",
        amount: "Amount",
        status: "Payment status",
        statusOptions: {
          pending: "Pending",
          paid: "Paid",
        },
        billingDay: "Monthly due day",
        isProportional: "First invoice proportional (pro-rata)",
        proportionalType: "Billing type",
        typeAmount: "Amount (R$)",
        typePercentage: "Percentage (%)",
        firstPaymentAmount: "1st invoice value",
        firstPaymentPercentage: "1st invoice percentage (%)",
        firstPaymentDate: "1st invoice due date",
        billingSummary: (date, amount, regularAmount, day) => `First invoice on ${date} for R$ ${amount}. Following that, R$ ${regularAmount} every ${day}th of the month.`,
      },
    },
    publicReschedule: {
      welcome: (name) => `Hello, ${name}`,
      subtitle: "Choose a new time for your appointment.",
      currentAppt: "Current Appointment",
      newDateLabel: "New Date",
      professionalLabel: "Professional",
      closedPage: "You can close this page now.",
      invalidLink: "Invalid Link",
      tryAgain: "Try Again",
      rescheduled: "Rescheduled!",
      rescheduledSubtitle: "Your appointment has been successfully rescheduled. You will receive a confirmation shortly.",
      step1: "Who would you like to be seen by?",
      step2: "Choose the day",
      step3: "Choose the time",
      searching: "Searching for times...",
      noSlots: "Unfortunately there are no free times for this professional on this day.",
      tip: "Tip: Try another professional or another date",
      selected: "Selected",
      confirm: "Confirm Rescheduling",
      processing: "Processing...",
      specialtyLabel: "Specialty",
      at: "at",
    },
    newMensalidadePage: {
      professional: "Professional",
      client: "Client",
      specialty: "Specialty",
      insurance: "Insurance",
      planValue: "Plan value",
      billingPeriod: "Billing period",
      billingDay: "Billing day",
      planName: "Plan name",
      markFirstAsPaid: "Mark first payment as paid",
      weekday: "Weekday",
      start: "Start",
      end: "End",
      createForToday: "Create for today (time has passed)",
      removeDay: "Remove day",
      addDay: "Add weekday",
      cancel: "Cancel",
      save: "Save",
      selectPlaceholder: "Select",
      comboboxSearch: "Search...",
      comboboxEmpty: "No items found.",
      billingPeriods: {
        monthly: "Monthly",
        quarterly: "Quarterly",
        semiannual: "Semiannual",
        annual: "Annual",
      },
      weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      paidByInsurance: "Paid by insurance",
    },
    evolutionModal: {
      title: "Appointment Evolution",
      audio: {
        title: "Voice Record",
        subtitle: "Speak and AI will transcribe for you",
        buttonStart: "Start",
        recording: "Recording...",
        buttonStop: "Stop",
        converting: "Converting audio to professional text...",
      },
      content: {
        label: "Evolution Description",
        improve: "Improve with AI",
        improving: "Improving...",
        placeholder: "Describe the patient's evolution here or use voice recording above...",
      },
      actions: {
        cancel: "Cancel",
        save: "Save Evolution",
      },
      toasts: {
        success: "Evolution saved!",
        error: "Error saving",
        audioConverted: "Audio converted!",
        audioError: "Audio conversion error",
        aiError: "AI error",
      },
    },
  },
};
